import { useEffect, useState } from 'react';
import { Pencil, Loader2 } from 'lucide-react';
import { getDisplayErrorMessage } from '@/lib/api';
import styles from './InlineEditRow.module.css';

/**
 * Phase 7g.4c Commit C — shared inline-edit row.
 *
 * Presentation-only component that owns the read / edit / busy /
 * error state machine for a single editable field. Side effects
 * (auth store sync, local state updates, navigation) live in the
 * parent's onSave callback. The row never reads from external
 * state; everything flows through props.
 *
 * Lifecycle:
 *   1. Read mode renders label + value (or emptyText if value is
 *      blank) + a pencil Edit button. The Edit button is hidden
 *      when editable is false.
 *   2. Click Edit → flip to edit mode. Draft seeds from value.
 *      Validator runs on every keystroke; the touched flag
 *      suppresses display of the validation error until the user
 *      has interacted with the input so opening edit mode never
 *      shows a scary error on a pre-filled value.
 *   3. Save button is disabled while busy or while the validator
 *      returns a non-null error. If the draft equals the original
 *      value (no actual change), Save flips back to read mode
 *      without firing onSave.
 *   4. Save success: row returns to read mode. Parent passes a
 *      fresh value prop on the next render.
 *   5. Save failure: the onSave promise's reject reason flows
 *      through getDisplayErrorMessage; the error displays inline,
 *      the row stays in edit mode so the user can retry without
 *      losing input.
 *   6. Cancel → discard the draft, return to read mode.
 *
 * Background-refetch handling: when the parent's value prop
 * changes while the row is in edit mode, the draft stays
 * untouched (mid-edit input survives). When the value changes
 * while in read mode, the display refreshes with the new value.
 *
 * Validator contract: returns the error message string to show
 * inline, or null when the value is acceptable. The Save button
 * gates on null. Returning a non-null error blocks Save even
 * before the user types (e.g. opening edit mode with an empty
 * required field will keep Save disabled, but the error stays
 * hidden until the user touches the input).
 */

export interface InlineEditRowProps {
  /** Label shown in read mode and above the input in edit mode. */
  label: string;
  /** Current displayed value. Drives read mode and seeds edit mode. */
  value: string;
  /** Placeholder in the input when the field is empty. */
  placeholder?: string;
  /** Input type. 'text' for name, 'tel' for phone. Defaults 'text'. */
  inputType?: 'text' | 'tel';
  /** Synchronous validator. Returns error message or null. Runs on
   *  every keystroke; the Save button is disabled while non-null. */
  validator?: (value: string) => string | null;
  /** Async save handler. Receives the trimmed new value. Throws on
   *  failure; the row catches throws and surfaces them inline. */
  onSave: (value: string) => Promise<void>;
  /** Display text when value is empty (e.g. "(not set)"). Defaults
   *  to an em-dash for back-compat with read-only cells. */
  emptyText?: string;
  /** When false, hide the Edit button (row is permanently read-only).
   *  Defaults true. */
  editable?: boolean;
  /** Optional maxLength on the input. */
  maxLength?: number;
  /** Optional aria-label override for the input. Defaults to the
   *  visible label. */
  inputAriaLabel?: string;
}

export function InlineEditRow({
  label,
  value,
  placeholder,
  inputType = 'text',
  validator,
  onSave,
  emptyText = '—',
  editable = true,
  maxLength,
  inputAriaLabel,
}: InlineEditRowProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [touched, setTouched] = useState(false);

  // Re-seed draft when the value prop changes while in read mode.
  // Mid-edit changes do not stomp the in-flight draft.
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const trimmed = draft.trim();
  const validationErr = validator ? validator(draft) : null;
  const displayErr = touched ? validationErr : null;
  const dirty = trimmed !== value.trim();
  const saveDisabled = busy || validationErr !== null;

  const onEdit = () => {
    setDraft(value);
    setErr('');
    setTouched(false);
    setEditing(true);
  };

  const onCancel = () => {
    setDraft(value);
    setErr('');
    setTouched(false);
    setEditing(false);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDraft(e.target.value);
    if (!touched) setTouched(true);
    if (err) setErr('');
  };

  const handleSave = async () => {
    if (saveDisabled) return;
    if (!dirty) {
      // No change. Skip the network call and flip back to read mode.
      setEditing(false);
      setTouched(false);
      return;
    }
    setErr('');
    setBusy(true);
    try {
      await onSave(trimmed);
      setEditing(false);
      setTouched(false);
    } catch (e) {
      setErr(await getDisplayErrorMessage(e, 'Could not save. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <div className={styles.row}>
        <span className={styles.label}>{label}</span>
        <span className={styles.value}>
          {value
            ? value
            : <span className={styles.emptyValue}>{emptyText}</span>}
        </span>
        {editable && (
          <button
            type="button"
            className={styles.editButton}
            onClick={onEdit}
            aria-label={`Edit ${label}`}
          >
            <Pencil size={14} />
            <span>Edit</span>
          </button>
        )}
      </div>
    );
  }

  // The displayed error precedence: the server-throw error (err)
  // wins over the live validation error because a server reject
  // is the freshest signal.
  const visibleErr = err || displayErr || '';

  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <div className={styles.editForm}>
        <input
          type={inputType}
          className={styles.editInput}
          value={draft}
          onChange={onChange}
          placeholder={placeholder}
          maxLength={maxLength}
          disabled={busy}
          autoFocus
          aria-label={inputAriaLabel ?? label}
          aria-invalid={visibleErr ? true : undefined}
        />
        <div className={styles.editActions}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.saveButton}
            onClick={() => void handleSave()}
            disabled={saveDisabled}
          >
            {busy ? <Loader2 size={12} className={styles.spinner} aria-hidden /> : null}
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
        {visibleErr && (
          <span className={styles.editError}>{visibleErr}</span>
        )}
      </div>
    </div>
  );
}
